import React, { useContext, useEffect, useState } from 'react';
import { AgendaContext } from '../../../contexts/AgendaContext';
import { useTheme } from '@emotion/react';
// Mui components
import { Avatar, Box, Button, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
// Components
import HorizontalDivider from '../../common/HorizontalDivider';
// Icons
import iconSearch from '../../../assets/icons/icon_search.svg';
import iconWarning from '../../../assets/icons/icon_warning.svg';
// CustomFunctions & Constants
import { compararStringsSinDiacriticos } from '../../../utils/Functions/filterOptions';
import { solicitudDeAlta } from '../../../utils/Constants/TiposDeSolicitud';
//Animation
const animation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

function PantallaProveedor({ modales, setOpenModals }) {
  const {
    proveedores,
    solicitudProps,
    setSolicitudProps,
    cuitNumber,
    proveedorSeleccionado,
    setProveedorSeleccionado,
    consultoriosContrato
  } = useContext(AgendaContext);


  const [proveedoresOptions, setProveedoresOptions] = useState(proveedores);
  const theme = useTheme();
  const handleOnChange = (event) => {
    setProveedoresOptions(
      proveedores.filter((opcion) =>
        compararStringsSinDiacriticos(opcion.label, event.target.value)
      )
    );
  };

  useEffect(() => {
    setSolicitudProps({
      ...solicitudProps,
      cuit: `${cuitNumber}`,
      proveedor: proveedorSeleccionado?.nombre,
      proveedorId: proveedorSeleccionado?.id,
      status: 'PENDIENTE',
      tipoSolicitud: solicitudDeAlta.tipoSolicitud,
      descripcionSolicitud: solicitudDeAlta.descripcionSolicitud
    });
  }, [proveedorSeleccionado]);

  const tieneTurnosAFuturo =
    consultoriosContrato.turnosFuturos?.tieneTurnos &&
    consultoriosContrato.turnosFuturos?.cantidadTurnos > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        ...(proveedores.length >= 12 && {
          paddingRight: '1em'
        })
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="h2">Proveedor de la agenda</Typography>
        <Typography variant="bodyM">
          Indicá el proveedor de la agenda que querés conectar con OSDE.
        </Typography>
      </Box>
      {tieneTurnosAFuturo && (
        <Box
          sx={{
            width: '100%',
            backgroundColor: theme.palette.alertBackground.main,
            border: `2px solid ${theme.palette.alertSecondary.main}`,
            borderRadius: '1rem',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            height: '8.15rem',
            animation: `${animation} 1s `
          }}>
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Box
              component="img"
              sx={{ width: '24px', height: '26px' }}
              alt="img not found"
              src={iconWarning}
            />
            <Typography variant="h4">
              Detectamos que tenés turnos planificados en la agenda de OSDE
            </Typography>
          </Box>

          <Typography variant="bodyM" sx={{ paddingLeft: '26px' }}>
            Te recomendamos verificar los turnos reservados. Tené en cuenta que esos horarios
            comprometidos permanecerán vigentes y no los podrás cancelar una vez aprobada tu
            solicitud.
          </Typography>
        </Box>
      )}

      {proveedores.length >= 12 && (
        <OutlinedInput
          placeholder="Buscá al proveedor de la agenda"
          onChange={(event) => handleOnChange(event)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1,
            marginBottom: '10px',
            width: '70%',
            maxWidth: '452px',
            maxHeight: '40px',
            '& .MuiOutlinedInput-input': {
              transform: 'translateY(0px)',
              padding: '0 0 0 .5em'
            }
          }}
          startAdornment={
            <InputAdornment position="end">
              <Box
                component="img"
                sx={{ width: '16px', height: '16px' }}
                alt="img not found"
                src={iconSearch}
              />
            </InputAdornment>
          }
        />
      )}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: '1.5em' }}>
        {proveedoresOptions
          .filter((proveedor) => proveedor.mostrar)
          .map((proveedor, index) => (
            <Box
              key={index}
              onClick={() => setProveedorSeleccionado(proveedor)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1,
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                padding: '1em',
                cursor: 'pointer',
                width: '33%',
                maxHeight: '108px',
                ...(proveedor === proveedorSeleccionado && {
                  backgroundColor: '#E7E9F7',
                  borderColor: '#1226AA'
                }),
                ...(proveedoresOptions.length >= 4 ? { maxWidth: '140px' } : { maxWidth: '218px' }),
                '&:hover': {
                  backgroundColor: '#E7E9F7'
                }
              }}>
              <Avatar sx={{ width: 40, height: 40 }}>{proveedor.label.substring(0, 2)}</Avatar>

              <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                <Typography variant="bodyS" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                  {proveedor.label}
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>
      <HorizontalDivider />
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={{ width: '80%' }}>
          <Typography variant="h4">¿Tenés otro proveedor?</Typography>
          <Typography variant="bodyS" sx={{ width: '100%' }}>
            Ingresá el nombre de tu proveedor actual, haremos lo posible para incorporarlo
            próximamente.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            width: '20%'
          }}>
          <Button
            variant="text"
            disableRipple
            onClick={() => setOpenModals({ ...modales, informarProveedor: true })}
            sx={{
              textDecoration: 'underline',
              color: '#1226AA',
              paddingBottom: 0,
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}>
            <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
              Informar proveedor
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default PantallaProveedor;
